/**
 * Measured latency, and the conditions it was measured under.
 *
 * Every number here came out of `packages/warden/src/bin/bench.rs` driving the
 * real query API against the real deployment, on the box that serves it.
 * Nothing is a target, an estimate, or copied from someone else's benchmark. The `provenance` block travels with the
 * numbers on purpose: a latency figure without its hardware, dataset and
 * concurrency is a marketing claim rather than a measurement, and this file
 * exists so the page cannot render one without the other.
 *
 * Re-run with `packages/warden/docs/benchmark.md` and replace both halves
 * together. If the two ever disagree, the numbers are wrong.
 */

export interface BenchmarkRun {
  /** The published endpoint, as named in the benchmark workspace. */
  endpoint: string;
  /** What the query does, in one line. */
  shape: string;
  /** Rows ClickHouse actually read, as it reported them. */
  rowsScanned: number;
  concurrency: number;
  /** Milliseconds, end to end, client side — socket, HTTP and JSON included. */
  p50: number;
  p90: number;
  p95: number;
  p99: number;
  requestsPerSecond: number;
  /** Whether the endpoint had a cache turned on. Never shown without saying so. */
  cached: boolean;
}

export interface BenchmarkProvenance {
  /** When the run was taken, ISO-8601 date. */
  measuredAt: string;
  hardware: string;
  clickhouseVersion: string;
  /** What the daemon and ClickHouse were allowed to use. */
  resources: string;
  dataset: string;
  datasetOnDisk: string;
  sortingKey: string;
  method: string;
  /** The honest caveat. Shown wherever the numbers are. */
  caveat: string;
}

export const BENCHMARK_PROVENANCE: BenchmarkProvenance = {
  measuredAt: "2026-08-23",
  hardware: "Intel Xeon E5-2650 v2 @ 2.60GHz, 32 threads, spinning disk behind a PERC H710P",
  clickhouseVersion: "25.3.14.14",
  resources: "ClickHouse capped at 12 CPUs and 24 GB, alongside the box's other services",
  dataset: "50,000,000 rows of web-analytics events",
  datasetOnDisk: "1.51 GiB",
  sortingKey: "(country, url, timestamp)",
  method:
    "Fixed concurrency held for 10 seconds after a 3-second warm-up, response body read in full, " +
    "connections reused. Percentiles are over every individual request.",
  caveat:
    "Measured on the production box, which is a 2013-era Xeon on rotational storage. " +
    "That hardware, not the software, is what sets the uncached numbers: the same build on " +
    "an M4 laptop with NVMe answers the same query in 1.96 ms rather than 7.09 ms. " +
    "These are the numbers you would get today.",
};

/**
 * The shapes deliberately span the range, because no single number is the
 * result. Two things are:
 *
 * Uncached, latency tracks **rows scanned** and almost nothing else — the same
 * table answers in 7 ms or 165 ms depending only on whether the query can use
 * the sorting key. That is why every response carries `rows_read`.
 *
 * Cached, it stops being a database question at all: the answer never leaves
 * the daemon's memory, so it costs tens of microseconds. That is a real number
 * and a real trade — the result can be up to the cache's lifetime stale, which
 * is why it is off unless an endpoint asks for it, and why `cached` is on every
 * row here so the fast ones can never be shown as though they were free.
 */
export const BENCHMARKS: BenchmarkRun[] = [
  {
    endpoint: "cached",
    shape: "Same query, answered from the endpoint's own cache",
    rowsScanned: 16384,
    concurrency: 1,
    p50: 0.26,
    p90: 0.26,
    p95: 0.27,
    p99: 0.28,
    requestsPerSecond: 4063,
    cached: true,
  },
  {
    endpoint: "cached",
    shape: "The cache under heavy load",
    rowsScanned: 16384,
    concurrency: 64,
    p50: 0.43,
    p90: 0.73,
    p95: 0.86,
    p99: 1.19,
    requestsPerSecond: 131551,
    cached: true,
  },
  {
    endpoint: "session_stats",
    shape: "Filter on the sorting-key prefix plus a 24-hour window",
    rowsScanned: 16384,
    concurrency: 1,
    p50: 7.09,
    p90: 13.03,
    p95: 13.93,
    p99: 14.33,
    requestsPerSecond: 120,
    cached: false,
  },
  {
    endpoint: "session_stats",
    shape: "The same endpoint under load",
    rowsScanned: 16384,
    concurrency: 8,
    p50: 7.25,
    p90: 9.95,
    p95: 13.73,
    p99: 18.73,
    requestsPerSecond: 972,
    cached: false,
  },
  {
    endpoint: "untuned",
    shape: "The same query with ClickHouse's default thread count",
    rowsScanned: 16384,
    concurrency: 1,
    p50: 15.35,
    p90: 16.89,
    p95: 17.39,
    p99: 19.07,
    requestsPerSecond: 67,
    cached: false,
  },
  {
    endpoint: "top_urls",
    shape: "Aggregate one country across every URL",
    rowsScanned: 5029888,
    concurrency: 1,
    p50: 54.3,
    p90: 64.11,
    p95: 66.81,
    p99: 69.62,
    requestsPerSecond: 18,
    cached: false,
  },
  {
    endpoint: "device_revenue",
    shape: "No usable key prefix, so it reads the whole table",
    rowsScanned: 50000000,
    concurrency: 1,
    p50: 165.02,
    p90: 177.54,
    p95: 187.32,
    p99: 206.13,
    requestsPerSecond: 6,
    cached: false,
  },
];

/** The fastest honest number: a cached endpoint, one caller at a time. */
export const HEADLINE_BENCHMARK: BenchmarkRun = BENCHMARKS[0]!;

/** The best throughput observed. Also a cached endpoint. */
export const PEAK_THROUGHPUT: BenchmarkRun = BENCHMARKS[1]!;

/** What an endpoint costs with no cache at all: the number to plan against. */
export const UNCACHED_BENCHMARK: BenchmarkRun = BENCHMARKS[2]!;

/**
 * The same query left on ClickHouse's own thread count, kept so the tuning is
 * visible rather than asserted. On this hardware capping threads is worth 2.2x.
 */
export const UNTUNED_BENCHMARK: BenchmarkRun = BENCHMARKS[4]!;

/**
 * How much of the table an endpoint had to read, as a share. The number that
 * explains the latency column better than any other.
 */
export const scannedShare = (run: BenchmarkRun, totalRows = 50_000_000): number =>
  run.rowsScanned / totalRows;
